import { useState, useEffect } from 'react';
import { Check, ArrowRight, Sparkles, Clock, Users, ThumbsUp, Timer, ShieldCheck } from 'lucide-react';
import { getApiUrl } from '../../utils/apiConfig';

const API_URL = getApiUrl();

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FeatureRow {
  label: string;
  tiers: [boolean | string, boolean | string, boolean | string];
}

export interface FeatureGroup {
  groupName: string;
  features: FeatureRow[];
}

export interface Bundle {
  name: string;
  duration: string;
  description: string;
  price: number;
  deliveryTime: string;
  popular: boolean;
  videoUrl?: string;
}

export interface PricingTab {
  bundles: Bundle[];
  featureGroups: FeatureGroup[];
}

interface SectionHeader {
  badge: string;
  title: string;
  subtitle: string;
  tab3DLabel: string;
  tab2DLabel: string;
  description3D: string;
  description2D: string;
  showMostPopular: boolean;
}

interface TabbedPricingProps {
  handleClaimBundle: (type: '3D' | '2D', tier: string, price: number, duration: string) => void;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_HEADER: SectionHeader = {
  badge: 'Manufacturing-Grade 3D',
  title: 'Simple, Project-Based Pricing',
  subtitle: 'No retainers, no hidden fees. Pay once per project — priced by video length.',
  tab3DLabel: '3D Video Package',
  tab2DLabel: '2D Video Package',
  description3D: 'Photorealistic 3D animation for machinery, mechanical assemblies, and industrial processes.',
  description2D: 'Clean 2D motion graphics for process flows, internal training, and technical explainers.',
  showMostPopular: true,
};

const DEFAULT_3D: PricingTab = {
  bundles: [
    { name: 'Snapshot',    duration: '45 seconds',  description: 'For showcasing a single product, component, or feature — sharp, focused, and built for sales.',     price: 2450, deliveryTime: '3–4 weeks',  popular: false },
    { name: 'Walkthrough', duration: '150 seconds', description: 'For explaining how a machine works — process flows, assembly sequences, and operational overviews.', price: 5900, deliveryTime: '5–6 weeks',  popular: true  },
    { name: 'Blueprint',   duration: '240 seconds', description: 'For complex systems and multi-stage processes that demand complete visual storytelling.',             price: 8900, deliveryTime: '8–10 weeks', popular: false },
  ],
  featureGroups: [
    {
      groupName: 'Production',
      features: [
        { label: '3D product modelling & texturing from CAD files', tiers: [true,  true,  true]  },
        { label: 'Full 3D environment build',                       tiers: [false, true,  true]  },
        { label: 'Animated mechanical sequences',                   tiers: [false, true,  true]  },
        { label: 'Multi-stage process animation',                   tiers: [false, false, true]  },
        { label: 'Professional script & voiceover (English)',       tiers: [true,  true,  true]  },
        { label: 'Licensed background music & sound design',        tiers: [true,  true,  true]  },
      ],
    },
    {
      groupName: 'Deliverables',
      features: [
        { label: 'Master video — MP4 + MOV',     tiers: ['1080p', '1080p & 4K', '1080p & 4K'] },
        { label: 'Format cuts',                  tiers: ['2 cuts', '3 cuts', 'All cuts']       },
        { label: '30-sec highlight reel',        tiers: [false, true,  true]  },
        { label: 'Static 3D render stills',      tiers: ['2 stills', '5 stills', '10 stills'] },
        { label: 'Full commercial usage rights', tiers: [true,  true,  true]  },
      ],
    },
    {
      groupName: 'Support',
      features: [
        { label: 'Revision rounds',                     tiers: ['2 rounds', 'Unlimited', 'Unlimited'] },
        { label: 'Dedicated project manager',           tiers: [false, true,  true]  },
        { label: 'Project portal & milestone tracking', tiers: [true,  true,  true]  },
        { label: 'NDA available on request',            tiers: [true,  true,  true]  },
      ],
    },
  ],
};

const DEFAULT_2D: PricingTab = {
  bundles: [
    { name: 'Snapshot',    duration: '45 seconds',  description: 'A punchy 2D explainer for a single process step or concept — built for fast deployment.',              price: 1200, deliveryTime: '1–2 weeks', popular: false },
    { name: 'Walkthrough', duration: '150 seconds', description: 'For internal training and process explainers that need clarity without the 3D budget.',                price: 3200, deliveryTime: '3–4 weeks', popular: true  },
    { name: 'Blueprint',   duration: '240 seconds', description: 'Full-length 2D production for training libraries, onboarding programmes, or multi-chapter explainers.', price: 5500, deliveryTime: '6–8 weeks', popular: false },
  ],
  featureGroups: [
    {
      groupName: 'Production',
      features: [
        { label: 'Custom 2D illustration & icon set',         tiers: [true,  true,  true]  },
        { label: 'Full custom 2D scene & character set',      tiers: [false, true,  true]  },
        { label: 'Multi-step process animation',              tiers: [false, true,  true]  },
        { label: 'Professional script & voiceover (English)', tiers: [true,  true,  true]  },
        { label: 'Licensed background music & sound design',  tiers: [true,  true,  true]  },
      ],
    },
    {
      groupName: 'Deliverables',
      features: [
        { label: 'Master video — MP4',           tiers: ['1080p', '1080p', '1080p']      },
        { label: 'Format cuts',                  tiers: ['2 cuts', '3 cuts', 'All cuts'] },
        { label: 'Full commercial usage rights', tiers: [true,  true,  true]  },
      ],
    },
    {
      groupName: 'Support',
      features: [
        { label: 'Revision rounds',                     tiers: ['2 rounds', 'Unlimited', 'Unlimited'] },
        { label: 'Dedicated project manager',           tiers: [false, true, true]  },
        { label: 'Project portal & milestone tracking', tiers: [true,  true, true]  },
        { label: 'NDA available on request',            tiers: [true,  true, true]  },
      ],
    },
  ],
};

const TRUST_STATS = [
  { icon: Users,       value: '650+', label: 'Projects Delivered'  },
  { icon: ThumbsUp,    value: '98%',  label: 'Client Satisfaction' },
];

// ─── Video Header ─────────────────────────────────────────────────────────────

function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('?')[0];
    const v = u.searchParams.get('v');
    if (v) return v;
    const embedMatch = u.pathname.match(/\/embed\/([^/?]+)/);
    if (embedMatch) return embedMatch[1];
  } catch {}
  return null;
}

function CardVideoHeader({ videoUrl, duration }: { videoUrl?: string; duration: string }) {
  const youtubeId = videoUrl ? getYouTubeId(videoUrl) : null;

  const isDirectVideo =
    videoUrl && !youtubeId && /\.(mp4|webm|ogg)(\?.*)?$/i.test(videoUrl);

  const embedSrc = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&modestbranding=1&rel=0&showinfo=0&disablekb=1&iv_load_policy=3&color=white`
    : null;

  return (
    <div
      className="relative w-full overflow-hidden rounded-t-3xl"
      style={{ height: '230px' }}
    >

      {/* ── Direct MP4 / self-hosted video ── */}
      {isDirectVideo && (
        <video
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* ── YouTube embed ── */}
      {embedSrc && (
        <div className="absolute inset-0">
          <iframe
            src={embedSrc}
            allow="autoplay; encrypted-media"
            allowFullScreen={false}
            className="absolute border-0"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) scale(1)',
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
            title="Bundle preview"
          />
          {/* Blocks YouTube logo — bottom-right */}
          <div
            className="absolute bottom-0 right-0 pointer-events-none"
            style={{ width: '140px', height: '50px', zIndex: 6 }}
          />
          {/* Blocks top-bar branding */}
          <div
            className="absolute top-0 left-0 right-0 pointer-events-none"
            style={{ height: '40px', zIndex: 6 }}
          />
        </div>
      )}

      {/* ── Placeholder ── */}
      {!isDirectVideo && !embedSrc && (
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, #1a1a3e 0%, #2d1a3e 100%)' }}
        >
          <svg
            className="absolute inset-0 w-full h-full"
            style={{ opacity: 0.2 }}
            viewBox="0 0 300 180"
            preserveAspectRatio="xMidYMid slice"
          >
            <rect x="30"  y="50"  width="60"  height="80"  rx="4" fill="none" stroke="#E87D3E" strokeWidth="1.5" />
            <rect x="110" y="65"  width="60"  height="50"  rx="4" fill="none" stroke="#E87D3E" strokeWidth="1.5" />
            <rect x="190" y="40"  width="80"  height="100" rx="4" fill="none" stroke="#E87D3E" strokeWidth="1.5" />
            <line x1="90"  y1="90" x2="110" y2="90" stroke="#E87D3E" strokeWidth="1.5" strokeDasharray="4,3" />
            <line x1="170" y1="90" x2="190" y2="90" stroke="#E87D3E" strokeWidth="1.5" strokeDasharray="4,3" />
          </svg>
        </div>
      )}

      {/* ── Premium gradient overlay — sits above video, below UI elements ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, rgba(15,10,30,0.75) 0%, rgba(15,10,30,0.1) 45%, transparent 100%)',
          zIndex: 8,
        }}
      />

      {/* ── Duration pill — top left ── */}
      <div
        className="absolute flex items-center gap-1.5 rounded-full pointer-events-none"
        style={{
          top: '16px',
          left: '16px',
          zIndex: 10,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          padding: '3px 8px 3px 6px',
        }}
      >
        <Clock style={{ width: '9px', height: '9px', color: '#E87D3E', flexShrink: 0 }} />
        <span style={{ fontSize: '10px', fontWeight: 600, color: '#ffffff', letterSpacing: '0.03em', lineHeight: 1 }}>
          {duration}
        </span>
      </div>

      {/* ── "Built to this standard" — bottom left, inside gradient ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '4px',
          right: '12px',
          zIndex: 10,
        }}
      >
        <span style={{ fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.8)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Built to this standard
        </span>
      </div>

    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TabbedPricing({ handleClaimBundle }: TabbedPricingProps) {
  const [activeTab, setActiveTab]         = useState<'3D' | '2D'>('3D');
  const [hoveredCard, setHoveredCard]     = useState<number | null>(null);
  const [loading, setLoading]             = useState(true);
  const [sectionHeader, setSectionHeader] = useState<SectionHeader>(DEFAULT_HEADER);
  const [tab3D, setTab3D]                 = useState<PricingTab>(DEFAULT_3D);
  const [tab2D, setTab2D]                 = useState<PricingTab>(DEFAULT_2D);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rHeader, r3D, r2D] = await Promise.all([
          fetch(`${API_URL}/bundles/pricing-header?t=${Date.now()}`, { headers: { 'Cache-Control': 'no-cache' } }),
          fetch(`${API_URL}/bundles/pricing-3d?t=${Date.now()}`,     { headers: { 'Cache-Control': 'no-cache' } }),
          fetch(`${API_URL}/bundles/pricing-2d?t=${Date.now()}`,     { headers: { 'Cache-Control': 'no-cache' } }),
        ]);
        if (rHeader.ok) { const res = await rHeader.json(); if (res.success && res.data) setSectionHeader(res.data); }
        if (r3D.ok)     { const res = await r3D.json();    if (res.success && res.data?.bundles) setTab3D(res.data); }
        if (r2D.ok)     { const res = await r2D.json();    if (res.success && res.data?.bundles) setTab2D(res.data); }
      } catch { /* fall back to defaults */ } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const current = activeTab === '3D' ? tab3D : tab2D;
  const header  = sectionHeader;

  if (loading) {
    return (
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6 lg:gap-8">
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-3xl bg-gray-100 animate-pulse h-[560px] border border-gray-200" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section
      id="pricing-section"
      className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">

        {/* ── Badge + Heading ── */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#2F2E5C]/5 border border-[#2F2E5C]/10 px-5 py-2.5 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold text-[#2F2E5C]">{header.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-[#2F2E5C]">
            {header.title}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {header.subtitle}
          </p>
        </div>

        {/* ── Trust pills ── */}
        <div className="flex flex-wrap items-center justify-center gap-2.5 mb-10">
          {TRUST_STATS.map(({ icon: Icon, value, label }) => (
            <div key={label} className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 shadow-sm rounded-full px-4 py-2">
              <div className="w-6 h-6 rounded-full bg-[#E87D3E]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-[#E87D3E]" />
              </div>
              <span className="text-sm font-black text-[#2F2E5C]">{value}</span>
              <span className="text-xs text-gray-400 font-medium">{label}</span>
            </div>
          ))}
        </div>

        {/* ── Tab switcher ── */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 rounded-2xl p-2">
            {(['3D', '2D'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 ${
                  activeTab === tab
                    ? 'bg-white text-[#2F2E5C] shadow-lg'
                    : 'text-gray-600 hover:text-[#2F2E5C]'
                }`}
              >
                {tab === '3D' ? header.tab3DLabel : header.tab2DLabel}
              </button>
            ))}
          </div>
        </div>

        {/* ── Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-4">
          {current.bundles.map((bundle, i) => {
            const isPopular = bundle.popular && header.showMostPopular;
            const isHovered = hoveredCard === i;

            const grouped = current.featureGroups
              .map(group => ({
                groupName: group.groupName,
                items: group.features
                  .map(row => ({ label: row.label, value: row.tiers[i] }))
                  .filter(f => f.value !== false),
              }))
              .filter(g => g.items.length > 0);

            return (
              <div
                key={bundle.name}
                className={`group relative transition-all duration-300 ${
                  isPopular ? 'md:scale-[1.05]' : 'hover:scale-[1.02]'
                }`}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Hover glow */}
                {isHovered && (
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#E87D3E]/30 to-[#E8B84D]/30 rounded-3xl blur-xl pointer-events-none" />
                )}

                {/* Popular badge */}
                {isPopular && (
                  <div style={{ position: 'absolute', top: '-14px', left: 0, right: 0, width: '100%', display: 'flex', justifyContent: 'center', zIndex: 20 }}>
                    <div className="bg-gradient-to-r from-[#D74444] to-[#E87D3E] text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                      ✦ MOST POPULAR
                    </div>
                  </div>
                )}

                {/* Card */}
                <div className={`relative flex flex-col bg-white rounded-3xl overflow-hidden transition-all duration-300 shadow-xl ${
                  isPopular
                    ? 'border-2 border-[#E87D3E]'
                    : 'border border-gray-200 hover:border-[#E87D3E]/50'
                }`}>

                  {/* ── Video header ── */}
                  <CardVideoHeader videoUrl={bundle.videoUrl} duration={bundle.duration} />

                  <div className="relative p-6 sm:p-8 flex flex-col flex-1">

                    {/* ── Name + delivery chip ── */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <h3
                        className="text-[#2F2E5C]"
                        style={{ fontSize: '1.5rem', letterSpacing: '-0.03em', lineHeight: 1, fontWeight: 700 }}
                      >
                        {bundle.name}
                      </h3>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold flex-shrink-0 mt-1 bg-gray-50 border border-gray-200 text-gray-500">
                        <Clock className="w-3 h-3 text-[#E87D3E]" />
                        {bundle.deliveryTime}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-500 leading-relaxed mb-6">{bundle.description}</p>

                    {/* ── Price ── */}
                    <div className="mb-6">
                      <p className="text-[11px] font-bold text-gray-400 tracking-widest uppercase mb-1">USD</p>
                      <p
                        className="text-[#2F2E5C] leading-none"
                        style={{ fontSize: '2.5rem', letterSpacing: '-0.05em', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
                      >
                        ${bundle.price.toLocaleString()}
                      </p>
                    </div>

                    {/* CTA */}
                    <button
                      onClick={() => handleClaimBundle(activeTab, bundle.name, bundle.price, bundle.duration)}
                      className={`w-full py-3.5 rounded-xl text-sm font-extrabold text-white transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 mb-8 ${
                        isPopular
                          ? 'bg-gradient-to-r from-[#D74444] to-[#E87D3E] shadow-lg shadow-orange-200/60'
                          : 'bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] shadow-sm'
                      }`}
                    >
                      Get a Free Proposal <ArrowRight className="w-4 h-4" />
                    </button>

                    {/* ── Grouped features ── */}
                    <div className="flex-1 space-y-8">
                      {grouped.map(group => (
                        <div key={group.groupName}>
                          <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest mb-4 pb-2 border-b border-gray-100">
                            {group.groupName}
                          </p>
                          <ul className="space-y-3">
                            {group.items.map((f, fi) => (
                              <li key={fi} className="flex items-start gap-3">
                                <div className="w-5 h-5 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check className="w-3.5 h-3.5 text-white" />
                                </div>
                                <span className="text-sm text-gray-700 leading-relaxed">
                                  {f.label}
                                  {typeof f.value === 'string' && (
                                    <span className="ml-1.5 font-semibold text-emerald-700">({f.value})</span>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}